import { useEffect, useRef, useState } from 'react'
import { createCompletionPoller, EVENT_OPERATION_POLL_INTERVAL_MS } from 'pages/EventsManagement/eventOperationCompletion'
import { EVENT_OPERATION_COMPLETION_TIMEOUT_MS } from 'pages/EventsManagement/eventOperationId'
import { announcementRefusalText, REQUEST_REFUSALS, REQUEST_REFUSED } from 'pages/EventsManagement/components/Tabs/Annoncements/announcementRefusals'

// An operation the API confirms later, on the socket (`receive`) or through a read that shows its result (`run({ execute, read,
// isComplete, matchesSocket, onSuccess, refusals })`), as an announcement's save or an author's command on a text. An uncertain result
// permits verification reads, never automatic writes. A refusal is told in words: `refusals` maps the operation's codes to their texts
// (announcementRefusalText).
export default function useConfirmedOperation() {
  const [state, setState] = useState({ phase: 'idle', message: '' })
  const current = useRef(null)
  const alive = useRef(true)
  const stop = () => { clearTimeout(current.current?.timer); current.current?.poller?.cancel() }
  useEffect(() => () => { alive.current = false; stop(); current.current = null }, [])
  const finish = result => {
    const operation = current.current
    if (!operation || !alive.current || operation.completed) return
    operation.completed = true
    stop(); current.current = null
    setState({ phase: 'idle', message: '' })
    operation.onSuccess?.(result)
  }
  const verify = async () => {
    const operation = current.current
    if (!operation) return
    try {
      const result = await operation.read()
      if (current.current !== operation) return
      if (operation.isComplete(result)) finish(result)
      else if (alive.current) setState({ phase: 'uncertain', message: 'The result is not confirmed yet. Check status again before taking another action.' })
    } catch {
      if (current.current === operation && alive.current) setState({ phase: 'uncertain', message: 'Could not check the result. Your request has not been repeated.' })
    }
  }
  const run = async options => {
    if (current.current) return
    const operation = { ...options }
    current.current = operation
    setState({ phase: 'pending', message: '' })
    operation.timer = setTimeout(() => {
      if (current.current !== operation || !alive.current) return
      operation.poller?.cancel()
      operation.expired = true
      setState({ phase: 'uncertain', message: 'Confirmation has not arrived. Check status; do not submit the same request again.' })
    }, EVENT_OPERATION_COMPLETION_TIMEOUT_MS)
    try {
      const response = await operation.execute()
      if (current.current !== operation || !alive.current) return
      if (operation.expired) return
      if (!response || response.success === false) throw new Error(response?.message || 'The request could not be confirmed.')
      operation.poller = createCompletionPoller({ read: operation.read, isComplete: operation.isComplete, intervalMs: EVENT_OPERATION_POLL_INTERVAL_MS })
      operation.poller.promise.then(({ status, result }) => { if (status === 'completed' && current.current === operation) finish(result) })
    } catch (error) {
      if (current.current !== operation || !alive.current) return
      stop()
      const status = error?.response?.status
      const rejected = status >= 400 && status < 500 && status !== 408
      if (rejected) current.current = null
      // A 401 is always the session's: the gateway's sign-in gates answer it.
      const code = status === 401 ? 'SESSION_EXPIRED' : error?.response?.data?.message
      setState({ phase: rejected ? 'error' : 'uncertain', message: rejected ? announcementRefusalText(code, { ...REQUEST_REFUSALS, ...operation.refusals }, REQUEST_REFUSED) : 'The result is uncertain. Check status before taking another action.' })
      if (rejected) operation.onRejected?.(error)
    }
  }
  const receive = payload => {
    const operation = current.current
    if (!operation?.matchesSocket?.(payload)) return
    if (payload.success) finish(payload)
    else { stop(); current.current = null; if (alive.current) setState({ phase: 'error', message: announcementRefusalText(payload.message, operation.refusals, 'The request could not be completed.') }) }
  }
  return { ...state, busy: state.phase === 'pending' || state.phase === 'uncertain', run, receive, verify }
}
